import React, { useMemo, useState } from 'react';
import { Wand2, ChevronDown, Info } from 'lucide-react';

const fmt = (v, d = 2) =>
    v === null || v === undefined || Number.isNaN(v)
        ? '—'
        : Number(v).toLocaleString('en-US', { maximumFractionDigits: d });

const fmtPrice = (v) => {
    if (!v && v !== 0) return '—';
    if (v >= 1000) return fmt(v, 2);
    if (v >= 1) return fmt(v, 4);
    return fmt(v, 8);
};

/**
 * Kaldıraç Sihirbazı — risk-based position sizing.
 *
 * Inputs:
 *   - Total capital ($)
 *   - Max risk ($ or %) willing to lose if the stop hits
 *
 * Uses the current coin's recommendation entry + stop_loss (or user override)
 * to compute:
 *   - stop_distance_pct = |entry - stop| / entry
 *   - position_notional = max_risk / stop_distance_pct
 *   - suggested_leverage = clip(position_notional / capital_at_risk, 1, 25)
 *   - margin = position_notional / leverage
 *
 * Also warns when leverage would exceed 10x (safety cap for a user who
 * doesn't want surprises).
 */
export default function LeverageWizard({ coinData, onApply }) {
    const [open, setOpen] = useState(false);
    const [capital, setCapital] = useState('');
    const [riskMode, setRiskMode] = useState('pct'); // 'pct' | 'usd'
    const [riskPct, setRiskPct] = useState('2');
    const [riskUsd, setRiskUsd] = useState('');
    const [maxLev, setMaxLev] = useState('10');

    const rec = coinData?.recommendation;
    const hasRec = rec && rec.action !== 'WAIT' && rec.entry && rec.stop_loss;

    const result = useMemo(() => {
        const cap = parseFloat(capital) || 0;
        const maxLeverage = Math.max(1, Math.min(50, parseFloat(maxLev) || 10));
        if (!cap || !hasRec) return null;

        const entry = rec.entry;
        const stop = rec.stop_loss;
        const side = rec.action === 'LONG' ? 'long' : 'short';

        // Risk in USD
        let maxRisk = 0;
        if (riskMode === 'pct') {
            const pct = parseFloat(riskPct) || 0;
            maxRisk = cap * (pct / 100);
        } else {
            maxRisk = parseFloat(riskUsd) || 0;
        }
        if (maxRisk <= 0) return null;

        // Stop distance %
        const stopDistPct = Math.abs(entry - stop) / entry;
        if (stopDistPct <= 0) return null;

        // Notional needed to make stop_distance_pct × notional == maxRisk
        const notional = maxRisk / stopDistPct;

        // Suggested leverage
        let suggestedLeverage = notional / cap;
        suggestedLeverage = Math.max(1, Math.min(maxLeverage, suggestedLeverage));

        // Round leverage down to a tidy integer step
        suggestedLeverage = Math.max(1, Math.floor(suggestedLeverage));

        // Recompute notional and margin given the clipped leverage
        const finalNotional = Math.min(notional, cap * suggestedLeverage);
        const margin = finalNotional / suggestedLeverage;

        // Final risk if stop hits
        const finalLoss = finalNotional * stopDistPct;
        const finalLossPct = (finalLoss / cap) * 100;

        // Potential gain at TP2 if present
        const tp2Pct = rec.tp2 ? Math.abs(rec.tp2 - entry) / entry : 0;
        const tp2Gain = tp2Pct * finalNotional;
        const tp2GainPct = (tp2Gain / cap) * 100;

        // Warnings
        const warnings = [];
        if (suggestedLeverage >= 10) warnings.push('Yüksek kaldıraç — küçük bir ters hareket büyük zarar.');
        if (margin < 5) warnings.push('Pozisyon çok küçük, borsa minimum emir büyüklüğünü karşılamayabilir.');
        if (notional > cap * maxLeverage) warnings.push(`Hedef risk için ${maxLeverage}x'ten yüksek kaldıraç gerekli — risk azaltıldı.`);

        return {
            entry,
            stop,
            side,
            notional: finalNotional,
            margin,
            leverage: suggestedLeverage,
            stop_dist_pct: stopDistPct * 100,
            max_risk: maxRisk,
            final_loss: finalLoss,
            final_loss_pct: finalLossPct,
            tp2_gain: tp2Gain,
            tp2_gain_pct: tp2GainPct,
            tp2: rec.tp2,
            warnings,
        };
    }, [capital, riskMode, riskPct, riskUsd, maxLev, rec, hasRec]);

    return (
        <div
            data-testid="leverage-wizard"
            className="border border-violet-500/25 bg-gradient-to-br from-violet-950/25 via-slate-800 to-slate-800 rounded-lg overflow-hidden"
        >
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="leverage-wizard-toggle"
                className="w-full px-3 py-2 flex items-center justify-between hover:bg-slate-700/40 transition-colors"
            >
                <div className="flex items-center gap-2 text-left">
                    <div className="h-7 w-7 rounded-md bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
                        <Wand2 className="h-3.5 w-3.5 text-violet-300" />
                    </div>
                    <div>
                        <div className="text-xs font-semibold text-slate-100">Kaldıraç Sihirbazı</div>
                        <div className="text-[10px] text-slate-400">
                            Elindeki para + alabileceğin risk → ideal pozisyon &amp; kaldıraç
                        </div>
                    </div>
                </div>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="px-3 pb-3 space-y-3 border-t border-slate-700/60">
                    {!hasRec && (
                        <div className="text-[11px] text-amber-300/80 bg-amber-500/10 border border-amber-500/20 rounded-sm px-2 py-1.5 flex items-start gap-1.5 mt-3">
                            <Info className="h-3 w-3 mt-0.5 shrink-0" />
                            <span>Önce bir coin seç ve sistemin AL/SAT önerisi (giriş + stop) olsun.</span>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-2 mt-3">
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                                Elindeki para ($)
                            </div>
                            <input
                                value={capital}
                                onChange={(e) => setCapital(e.target.value)}
                                type="number"
                                step="any"
                                placeholder="1000"
                                data-testid="wizard-capital"
                                className="w-full h-8 bg-slate-900 border border-slate-700 rounded-sm px-2 font-mono text-sm text-slate-100"
                            />
                        </label>
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                                Max kaldıraç
                            </div>
                            <select
                                value={maxLev}
                                onChange={(e) => setMaxLev(e.target.value)}
                                data-testid="wizard-max-leverage"
                                className="w-full h-8 bg-slate-900 border border-slate-700 rounded-sm px-2 font-mono text-sm text-slate-100"
                            >
                                <option value="3">3x — Güvenli</option>
                                <option value="5">5x — Orta</option>
                                <option value="10">10x — Agresif</option>
                                <option value="20">20x — Yüksek risk</option>
                                <option value="50">50x — Çok yüksek</option>
                            </select>
                        </label>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <div className="text-[10px] uppercase tracking-wider text-slate-400">
                                Alabileceğin max risk
                            </div>
                            <div className="inline-flex rounded-sm border border-slate-700 overflow-hidden text-[9px]">
                                <button
                                    onClick={() => setRiskMode('pct')}
                                    data-testid="wizard-risk-mode-pct"
                                    className={`px-2 h-5 ${riskMode === 'pct' ? 'bg-violet-500/20 text-violet-200' : 'text-slate-400'}`}
                                >
                                    %
                                </button>
                                <button
                                    onClick={() => setRiskMode('usd')}
                                    data-testid="wizard-risk-mode-usd"
                                    className={`px-2 h-5 border-l border-slate-700 ${riskMode === 'usd' ? 'bg-violet-500/20 text-violet-200' : 'text-slate-400'}`}
                                >
                                    $
                                </button>
                            </div>
                        </div>
                        {riskMode === 'pct' ? (
                            <input
                                value={riskPct}
                                onChange={(e) => setRiskPct(e.target.value)}
                                type="number"
                                step="any"
                                placeholder="2 (sermayenin %2'si)"
                                data-testid="wizard-risk-pct"
                                className="w-full h-8 bg-slate-900 border border-slate-700 rounded-sm px-2 font-mono text-sm text-slate-100"
                            />
                        ) : (
                            <input
                                value={riskUsd}
                                onChange={(e) => setRiskUsd(e.target.value)}
                                type="number"
                                step="any"
                                placeholder="20 (en fazla 20$ kaybedebilirim)"
                                data-testid="wizard-risk-usd"
                                className="w-full h-8 bg-slate-900 border border-slate-700 rounded-sm px-2 font-mono text-sm text-slate-100"
                            />
                        )}
                        <div className="text-[9px] text-slate-400 mt-0.5">
                            💡 Profesyoneller her işlemde sermayenin max %1-2'sini riske atar.
                        </div>
                    </div>

                    {result && (
                        <div className="space-y-2">
                            <div className="bg-slate-900/60 border border-violet-500/20 rounded-md p-2.5">
                                <div className="text-[10px] uppercase tracking-wider text-violet-300 mb-1.5 font-semibold">
                                    Önerim
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-[11px]">
                                    <div>
                                        <div className="text-slate-400 text-[9px] uppercase">Marjin</div>
                                        <div className="font-mono text-slate-100 font-semibold">${fmt(result.margin, 2)}</div>
                                    </div>
                                    <div>
                                        <div className="text-slate-400 text-[9px] uppercase">Kaldıraç</div>
                                        <div className={`font-mono font-bold ${result.leverage >= 10 ? 'text-amber-300' : 'text-emerald-300'}`}>
                                            {result.leverage}x
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-slate-400 text-[9px] uppercase">Nominal</div>
                                        <div className="font-mono text-slate-100">${fmt(result.notional, 2)}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-[11px]">
                                <div className="bg-rose-500/10 border border-rose-500/25 rounded-sm px-2 py-1.5">
                                    <div className="text-[9px] uppercase tracking-wider text-rose-300">
                                        Stop vurursa
                                    </div>
                                    <div className="font-mono text-rose-200 font-semibold">
                                        -${fmt(result.final_loss, 2)}
                                    </div>
                                    <div className="text-[9px] text-rose-300/80">
                                        -%{result.final_loss_pct.toFixed(2)} sermaye
                                    </div>
                                </div>
                                {result.tp2 && (
                                    <div className="bg-emerald-500/10 border border-emerald-500/25 rounded-sm px-2 py-1.5">
                                        <div className="text-[9px] uppercase tracking-wider text-emerald-300">
                                            TP2 vurursa
                                        </div>
                                        <div className="font-mono text-emerald-200 font-semibold">
                                            +${fmt(result.tp2_gain, 2)}
                                        </div>
                                        <div className="text-[9px] text-emerald-300/80">
                                            +%{result.tp2_gain_pct.toFixed(2)} sermaye
                                        </div>
                                    </div>
                                )}
                            </div>

                            {result.warnings.length > 0 && (
                                <ul className="space-y-0.5">
                                    {result.warnings.map((w) => (
                                        <li key={w} className="text-[10px] text-amber-300/80 flex items-start gap-1">
                                            <span>⚠</span>
                                            <span>{w}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <button
                                onClick={() => onApply?.({
                                    entry: result.entry,
                                    investment: result.margin.toFixed(2),
                                    leverage: result.leverage,
                                    side: result.side,
                                })}
                                data-testid="wizard-apply-btn"
                                className="w-full h-8 rounded-sm bg-violet-500 hover:bg-violet-400 text-white text-xs font-medium uppercase tracking-wider transition-colors"
                            >
                                Pozisyon Formuna Uygula →
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
